import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { projects } from "../../data/content";
import { SectionHeading, staggerContainer, staggerItem } from "./Reveal";
import { ProjectDiagram } from "./ProjectDiagram";

const ProjectCard = ({ project, featured }) => (
  <motion.article
    variants={staggerItem}
    data-testid={`project-card-${project.name.toLowerCase()}`}
    className={`group relative flex flex-col rounded-3xl border border-[var(--border-subtle)] bg-surface p-7 md:p-9 transition-all duration-300 hover:border-[var(--border-strong)] hover:bg-surface-elevated hover:-translate-y-1 ${
      featured ? "md:col-span-3" : "md:col-span-2"
    }`}
  >
    {featured && (
      <div className="mb-6">
        <ProjectDiagram name={project.name} />
      </div>
    )}
    <div className="flex items-start justify-between gap-4 mb-5">
      <div>
        <h3 className="font-heading font-bold text-xl md:text-2xl tracking-tight text-primary-c">
          {project.name}
        </h3>
        <p className="mt-1 text-sm text-secondary-c">{project.tagline}</p>
      </div>
      <ArrowUpRight
        className="w-5 h-5 text-tertiary-c transition-all duration-300 group-hover:text-primary-c group-hover:-translate-y-0.5 group-hover:translate-x-0.5 shrink-0"
        strokeWidth={1.5}
      />
    </div>

    <div className="space-y-4 flex-1">
      <div>
        <p className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-tertiary-c mb-1.5">
          Problem
        </p>
        <p className="text-sm text-secondary-c leading-relaxed">
          {project.problem}
        </p>
      </div>
      <div>
        <p className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-tertiary-c mb-1.5">
          Solution
        </p>
        <p className="text-sm text-secondary-c leading-relaxed">
          {project.solution}
        </p>
      </div>
      {featured && (
        <div>
          <p className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-tertiary-c mb-1.5">
            Technical note
          </p>
          <p className="text-sm text-secondary-c leading-relaxed">
            {project.highlight}
          </p>
        </div>
      )}
    </div>

    <div className="mt-6 pt-5 border-t border-[var(--border-subtle)] flex flex-wrap gap-2">
      {project.stack.map((s) => (
        <span
          key={s}
          className="font-mono-tech text-[11px] tracking-wide bg-white/[0.04] border border-white/10 text-secondary-c rounded-full px-2.5 py-1"
        >
          {s}
        </span>
      ))}
    </div>
  </motion.article>
);

export const Projects = () => {
  const featured = projects.filter((p) => p.featured);
  const rest = projects.filter((p) => !p.featured);

  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="relative border-y border-[var(--border-subtle)] bg-surface"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <SectionHeading
          index="03"
          overline="Selected work"
          title="Products, told as case studies."
          description="A selection of systems I've designed and shipped — each framed by the problem it solved and the engineering decision behind it."
        />

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-6"
        >
          {featured.map((p) => (
            <ProjectCard key={p.name} project={p} featured />
          ))}
          {rest.map((p) => (
            <ProjectCard key={p.name} project={p} featured={false} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};
